import { bookingClient } from 'lib/axios'
import { parseCookies } from 'nookies'
import { isMobile } from 'react-device-detect'
import getAcceptLanguageByLocale from '../../../lib/getAcceptLanguageByLocale'
import getLocaleHeaderByLocale from '../../../lib/getLocaleHeaderByLocale'

const API_LOCKER_DETAIL = '/api/app/v1/locations/lockers/'
const API_LOCKER_VALIDATE = '/api/jre/app/v1/bookings/validate'
const API_REQUEST_LOCKER_BOOKING = '/api/jre/app/v1/bookings'
const API_CANCEL_LOCKER_BOOKING = '/api/jre/app/v1/bookings/'
const API_LIST_LOCKER_BOOKINGS = '/api/jre/app/v1/bookings'
const API_FETCH_PAYMENT_INTENTS = '/api/jre/app/v1/bookings/payment_intents'
const API_CHECK_AVAILABILITY = (lockerId: string) =>
  `/api/app/v1/locations/lockers/${lockerId}/check_available`

export const getLockerDetail = async (lockerId: string, locale?: string) => {
  const _locale = getLocaleHeaderByLocale(locale || 'ja')
  const _acceptLanguage = getAcceptLanguageByLocale(locale || 'ja')

  const response = await bookingClient.get(API_LOCKER_DETAIL + lockerId, {
    headers: { 'Accept-Language': _acceptLanguage, LOCALE: _locale },
  })

  const { locker } = response.data

  return locker
}

export type LockerBookingParams = {
  locker_id: string
  user_id: string
  booked_from: string
  booked_to: string
  small_box_count: number
  medium_box_count: number
  large_box_count: number
}

export const validateLockerBooking = async (params: LockerBookingParams, locale?: string) => {
  try {
    const cookies = parseCookies()
    const _locale = getLocaleHeaderByLocale(locale || 'ja')
    const _acceptLanguage = getAcceptLanguageByLocale(locale || 'ja')

    const response = await bookingClient.get(API_LOCKER_VALIDATE, {
      params: {
        ...params,
        source: 0, // 0: web, 2: mobile
      },
      headers: {
        'Accept-Language': _acceptLanguage,
        LOCALE: _locale,
        authorization: `Token ${cookies.jwt_token}`,
      },
    })

    const { valid } = response.data

    return {
      isOk: !!valid,
    }
  } catch (error) {
    const { errors } = error.response.data
    return {
      isOk: false,
      message: errors.base[0],
    }
  }
}

export const requestLockerBooking = async (params: LockerBookingParams, locale: string) => {
  try {
    const cookies = parseCookies()
    const _locale = getLocaleHeaderByLocale(locale || 'ja')
    const _acceptLanguage = getAcceptLanguageByLocale(locale || 'ja')

    const response = await bookingClient.post(
      API_REQUEST_LOCKER_BOOKING,
      {
        ...params,
        source: isMobile ? 2 : 0,
      },
      {
        headers: {
          'Accept-Language': _acceptLanguage,
          LOCALE: _locale,
          authorization: `Token ${cookies.jwt_token}`,
        },
      }
    )

    return {
      isOk: true,
      data: response.data,
    }
  } catch (error) {
    const { errors } = error.response.data
    return {
      isOk: false,
      error: errors?.base?.[0] || errors?.[0]?.message || error.message,
    }
  }
}

export const getLockerBookings = async (page: number, locale: string = 'ja') => {
  try {
    const cookies = parseCookies()
    const _locale = getLocaleHeaderByLocale(locale || 'ja')
    const _acceptLanguage = getAcceptLanguageByLocale(locale || 'ja')

    const response = await bookingClient.get(API_LIST_LOCKER_BOOKINGS, {
      params: {
        page,
      },
      headers: {
        'Accept-Language': _acceptLanguage,
        LOCALE: _locale,
        authorization: `Token ${cookies.jwt_token}`,
      },
    })

    return response
  } catch (error) {
    return []
  }
}

export const cancelLockerBooking = async (
  bookingId: string,
  locale: string = 'ja',
  isSuspend: boolean = false
) => {
  try {
    const cookies = parseCookies()
    const _locale = getLocaleHeaderByLocale(locale || 'ja')
    const _acceptLanguage = getAcceptLanguageByLocale(locale || 'ja')

    await bookingClient.patch(
      API_CANCEL_LOCKER_BOOKING + bookingId + '/cancel',
      {
        is_suspend: isSuspend,
      },
      {
        headers: {
          'Accept-Language': _acceptLanguage,
          LOCALE: _locale,
          authorization: `Token ${cookies.jwt_token}`,
        },
      }
    )

    return {
      isOk: true,
    }
  } catch (error) {
    return {
      isOk: false,
      message: error.message,
    }
  }
}

export type FetchLockerPaymentIntentsParams = {
  lockerId: string
  bookedFrom: string
  bookedTo: string
  small: number
  medium: number
  large: number
}

export const fetchLockerPaymentIntents = async (
  params: FetchLockerPaymentIntentsParams,
  accessToken: string
) => {
  try {
    const response = await bookingClient.post(
      API_FETCH_PAYMENT_INTENTS,
      {
        locker_id: params.lockerId,
        booked_from: params.bookedFrom,
        booked_to: params.bookedTo,
        small_box_count: params.small,
        medium_box_count: params.medium,
        large_box_count: params.large,
        source: 2,
      },
      {
        headers: {
          Authorization: `Token ${accessToken}`,
        },
      }
    )

    return { isSuccess: true, data: response.data }
  } catch (error) {
    return { isSuccess: false }
  }
}

type CheckLockerBookingAvailabilityParams = {
  lockerId: string
  bookedFrom: string
  bookedTo: string
}

export const checkLockerBookingAvailability = async (
  params: CheckLockerBookingAvailabilityParams,
  locale: string = 'ja'
) => {
  try {
    const cookies = parseCookies()
    const _locale = getLocaleHeaderByLocale(locale || 'ja')
    const _acceptLanguage = getAcceptLanguageByLocale(locale || 'ja')

    const response = await bookingClient.post(
      API_CHECK_AVAILABILITY(params.lockerId),
      {
        booked_from: params.bookedFrom,
        booked_to: params.bookedTo,
      },
      {
        headers: {
          'Accept-Language': _acceptLanguage,
          LOCALE: _locale,
          authorization: `Token ${cookies.jwt_token}`,
        },
      }
    )

    const { is_available } = response.data

    return {
      isOk: is_available,
    }
  } catch (error) {
    return {
      isOk: false,
      message: error.message,
    }
  }
}
